import { Fragment, ReactNode } from "react";
import classNames from "classnames";
import { RecordCardsProps } from "./RecordCards.types";
import { RecordTableColumn } from "./RecordTable.types";
import { useResponsiveClassName } from "./forcedBreakpoint";

const isBlank = (value: unknown) =>
  value === null || value === undefined || value === "";

function cell(column: RecordTableColumn, row: Record<string, unknown>): ReactNode {
  return column.accessor ? column.accessor(row) : String(row[column.key] ?? "");
}

/**
 * Rows as cards, from the same columns a table would use: the first is the card's
 * title, badge columns sit beside it, subtitle columns under it, and the rest are
 * label and value lines - with anything attached to a line under its value. A value the
 * row doesn't have is left out rather than shown empty. The cards in a row share its
 * height, their buttons along the bottom.
 */
export function RecordCards({
  columns,
  rows,
  emptyMessage = "No records found.",
  emptyTone = "info",
  rowActions,
  rowKey,
}: RecordCardsProps) {
  // Three across from lg, two from md, one on a phone - forced in the designer's previews.
  const cardColumn = useResponsiveClassName({
    viewport: "col-12 col-md-6 col-lg-4",
    mobile: "col-12",
    tablet: "col-6",
    desktop: "col-4",
  });

  // Dark text on white: muted text, or the grey info alert, was grey on the page's grey.
  // A failed read keeps the red alert.
  if (rows.length === 0) {
    return (
      <div
        className={
          emptyTone === "danger"
            ? "alert alert-danger mb-0"
            : "bg-white border rounded-2 p-3 text-body mb-0"
        }
        role="status"
      >
        {emptyMessage}
      </div>
    );
  }

  const [titleColumn, ...rest] = columns;
  const attached = rest.filter((column) => column.cardAttachTo);
  const placed = rest.filter((column) => !column.cardAttachTo);
  const badgeColumns = placed.filter((column) => column.cardSlot === "badge");
  const subtitleColumns = placed.filter((column) => column.cardSlot === "subtitle");
  const lineColumns = placed.filter(
    (column) => !column.cardSlot || column.cardSlot === "line"
  );

  return (
    <div className="row g-3">
      {rows.map((row, index) => {
        const actions = rowActions?.(row);
        const has = (column: RecordTableColumn) => !isBlank(row[column.key]);
        const subtitles = subtitleColumns.filter(has);

        return (
          <div className={classNames(cardColumn, "d-flex")} key={rowKey?.(row) ?? index}>
            <div className="card w-100">
              <div className="card-body flex-grow-1 d-flex flex-column gap-3">
                <div>
                  <div className="d-flex align-items-start justify-content-between gap-2">
                    {titleColumn && (
                      <h3 className="fs-6 fw-semibold mb-0">{cell(titleColumn, row)}</h3>
                    )}
                    <div className="d-flex flex-wrap justify-content-end gap-1">
                      {badgeColumns.filter(has).map((column) => (
                        <span key={column.key}>{cell(column, row)}</span>
                      ))}
                    </div>
                  </div>
                  {subtitles.length > 0 && (
                    <p className="small text-muted mb-0">
                      {subtitles.map((column, position) => (
                        <span key={column.key}>
                          {position > 0 && " | "}
                          {cell(column, row)}
                        </span>
                      ))}
                    </p>
                  )}
                </div>
                <dl className="row gy-1 mb-0">
                  {lineColumns.filter(has).map((column) => (
                    <Fragment key={column.key}>
                      <dt className="col-4 fw-normal text-muted">{column.label}</dt>
                      <dd className="col-8 mb-0 text-break">
                        {cell(column, row)}
                        {attached
                          .filter((extra) => extra.cardAttachTo === column.key && has(extra))
                          .map((extra) => (
                            <div className="mt-1" key={extra.key}>
                              {cell(extra, row)}
                            </div>
                          ))}
                      </dd>
                    </Fragment>
                  ))}
                </dl>
              </div>
              {actions && (
                <div className="card-footer bg-transparent d-flex justify-content-end gap-2">
                  {actions}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
